import style from './Transaction.css';
import CardFilledIcon from '../../shared/Icons/CardFilledIcon';

import { renderIf } from '../../../utils/render.utils';
import FeeFilledIcon from '../../shared/Icons/FeeFilledIcon';
import WithdrawFilledIcon from '../../shared/Icons/WithdrawFilledIcon';
import DepositFilledIcon from '../../shared/Icons/DepositFilledIcon';
import ReversalIcon from '../../shared/Icons/ReversalIcon';
import useConfig from '../../../config/config.context';
import { _className } from '../../../utils/classname.utils';
import TransferCreditFilledIcon from '../../shared/Icons/TransferCreditFilledIcon';
import useTranslation from '../../../localization/i18n.context';
import CaasStorageKeys from '../../../constants/caas.storage.keys';
import CashbackIcon from '../../shared/Icons/CashbackIcon';
import TransferDebitFilledIcon from '../../shared/Icons/TransferDebitFilledIcon';
import CreditCreditFilledIcon from '../../shared/Icons/CreditCreditFilledIcon';
import CreditDebitFilledIcon from '../../shared/Icons/CreditDebitFilledIcon';

const TransactionType = {
  CASHBACK: 'CASHBACK',
  FEE: 'FEE',
  WITHDRAWAL: 'WITHDRAWAL',
  PURCHASE: 'PURCHASE',
  DEPOSIT: 'DEPOSIT',
  TRANSFER: 'TRANSFER',
  CREDIT: 'CREDIT',
  REVERSAL: 'REVERSAL',
  REFUND: 'REFUND',

  TRANSFER_DEBIT: 'TRANSFER_DEBIT',
  TRANSFER_CREDIT: 'TRANSFER_CREDIT',

  CREDIT_DEBIT: 'CREDIT_DEBIT',
  CREDIT_CREDIT: 'CREDIT_CREDIT',
};

const SignType = {
  CREDIT: 'CREDIT',
  DEBIT: 'DEBIT',
};

const SourceType = {
  TOP_UP_POS: 'TOP_UP_POS',
  SEND_MONEY_TO_COMPANY: 'SEND_MONEY_TO_COMPANY',
};

const EntityType = {
  USER: 'USER',
  ORGANIZATION: 'ORGANIZATION',
};

const Icons = {
  [TransactionType.PURCHASE]: <CardFilledIcon />,
  [TransactionType.DEPOSIT]: <DepositFilledIcon />,
  [TransactionType.WITHDRAWAL]: <WithdrawFilledIcon />,
  [TransactionType.REVERSAL]: <ReversalIcon />,
  [TransactionType.CASHBACK]: <CashbackIcon />,
  [TransactionType.FEE]: <FeeFilledIcon />,
  [TransactionType.TRANSFER_DEBIT]: <TransferDebitFilledIcon />,
  [TransactionType.TRANSFER_CREDIT]: <TransferCreditFilledIcon />,
  [TransactionType.CREDIT_DEBIT]: <CreditDebitFilledIcon />,
  [TransactionType.CREDIT_CREDIT]: <CreditCreditFilledIcon />,
  [TransactionType.REFUND]: <ReversalIcon />,
};

const TransactionIcon = ({ type, sign }) => {
  if (type === TransactionType.TRANSFER) {
    return Icons[`${TransactionType.TRANSFER}_${sign}`];
  }

  if (type === TransactionType.CREDIT) {
    return Icons[`${TransactionType.CREDIT}_${sign}`];
  }

  return Icons[type];
};

export default function Transaction({ className = '', transaction, ...props }) {
  const { config } = useConfig();
  const { t, getOrganization } = useTranslation();

  const [currency, amountNumber] = transaction.amount.split(' ');

  const isCredit = transaction.sign === SignType.CREDIT;

  function getTitle(transaction) {
    if (
      transaction.type === TransactionType.TRANSFER &&
      transaction.raw?.metadata?.source === SourceType.SEND_MONEY_TO_COMPANY
    ) {
      return (
        t('Transfer to') +
        ' ' +
        getOrganization(transaction.raw?.metadata?.organizationCodeFrom)
      );
    }

    if (transaction.type === TransactionType.REFUND) {
      return 'REFUND';
    }

    if (transaction.type === TransactionType.CASHBACK) {
      return 'CASHBACK';
    }

    if (
      transaction.type === TransactionType.FEE ||
      transaction.type === TransactionType.WITHDRAWAL ||
      transaction.type === TransactionType.DEPOSIT ||
      transaction.type === TransactionType.REVERSAL
    ) {
      return transaction.source;
    }

    if (
      transaction.type === TransactionType.TRANSFER &&
      transaction.sign === SignType.CREDIT &&
      transaction.raw?.metadata?.entityTypeFrom === EntityType.USER
    ) {
      return transaction.raw?.metadata?.userFromName;
    }

    if (
      transaction.type === TransactionType.TRANSFER &&
      transaction.sign === SignType.CREDIT &&
      transaction.raw?.metadata?.entityTypeFrom === EntityType.ORGANIZATION
    ) {
      return getOrganization(transaction.raw?.metadata?.organizationCodeFrom);
    }

    if (
      transaction.type === TransactionType.TRANSFER &&
      transaction.sign === SignType.DEBIT &&
      transaction.source === SourceType.TOP_UP_POS
    ) {
      return 'POS';
    }

    if (
      transaction.type === TransactionType.TRANSFER &&
      transaction.sign === SignType.DEBIT
    ) {
      return transaction.raw?.metadata?.userToName;
    }

    if (
      transaction.type === TransactionType.CREDIT &&
      transaction.sign === SignType.CREDIT
    ) {
      return 'From credit limit';
    }

    if (
      transaction.type === TransactionType.CREDIT &&
      transaction.sign === SignType.DEBIT
    ) {
      return 'To current account';
    }

    return transaction.merchant;
  }

  function getSubtitle(transaction) {
    if (transaction.type === TransactionType.CREDIT) {
      return TransactionType.TRANSFER;
    }

    // ATTENTION: This is a function just in case we need to change the subtitle based on the type or maybe another new param. Now we only will return the same type
    return transaction.type;
  }

  return (
    <div className={`${className} ${style['list-item']}`} {...props}>
      <span className={style['icon']}>
        <TransactionIcon type={transaction.type} sign={transaction.sign} />
      </span>
      <div className={style.content}>
        <span className={style.title}>{t(getTitle(transaction))}</span>
        {renderIf(
          transaction.type &&
            [
              TransactionType.DEPOSIT,
              TransactionType.WITHDRAWAL,
              TransactionType.FEE,
              TransactionType.REVERSAL,
              TransactionType.TRANSFER,
              TransactionType.CREDIT,
            ].includes(transaction.type),
          <span className={style.subtitle}>{t(getSubtitle(transaction))}</span>,
        )}
      </div>
      <div
        className={`${_className(isCredit, 'text-success')}  ${style.amount}`}
      >
        <span
          className={_className(
            !config[CaasStorageKeys.STORAGE].showBalance,
            'text-blur',
          )}
        >
          <span>
            {/*ATTENTION: The following condition before currency is a workaround because current transaction endpoint returns always positve numbers. In case it change, it will not duplicate the - symbol */}
            {!isCredit && !amountNumber.includes('-') ? '-' : ''}
            {currency}
          </span>
          <span>{amountNumber}</span>
        </span>
      </div>
    </div>
  );
}
Transaction.Type = TransactionType;
